import React from 'react';
import TimedPill from './TimedPill';

/**
 * PillList - Renders a list of TimedPill components
 * @param {string[]} items - Array of labels to display
 * @param {function} getTooltip - Function that returns tooltip text for an item
 * @param {string} idPrefix - Prefix for unique pill IDs
 * @param {string|Object|null} alignmentStatus - Alignment status:
 *   - string: Applied uniformly to all pills ('match' | 'mismatch' | null)
 *   - object with 'items': Per-item alignment { overall: string, items: { [label]: string } }
 * @param {boolean} hideWhenEmpty - When true, a list with nothing in it renders
 *   NOTHING at all: no dash, no container, no vertical space. Off by default,
 *   so every existing surface keeps drawing the dash placeholder byte for byte.
 */
const PillList = ({ items, getTooltip, idPrefix, alignmentStatus = null, hideWhenEmpty = false }) => {
  // Filter out null, undefined, and empty string items
  const validItems = items?.filter(it => it != null && it !== '') || [];

  // Only the GENUINELY empty branch is affected. A caller that passes a dash as
  // a real ITEM -- ProfileFront's work-type and schedule rows do, and give it a
  // tooltip of its own -- has a filled list of one and renders as it always has,
  // with or without this flag.
  if (hideWhenEmpty && validItems.length === 0) return null;

  // Helper to get alignment status for a specific item
  const getItemAlignment = (item) => {
    // If alignmentStatus is an object with 'items', use per-item alignment
    if (alignmentStatus && typeof alignmentStatus === 'object' && alignmentStatus.items) {
      // Presence, not truthiness. A per-item null is a DECISION ("we cannot
      // compare this one"), and falling back to `overall` for it would draw a
      // match ring on a pill nothing was verified about. Contract type sets
      // every item it knows about, so this is unchanged there; schedule
      // deliberately leaves un-comparable terms null.
      if (Object.prototype.hasOwnProperty.call(alignmentStatus.items, item)) {
        return alignmentStatus.items[item];
      }
      return alignmentStatus.overall || null;
    }
    // Otherwise use the uniform status (backwards compatible)
    return alignmentStatus;
  };

  return (
    <div className="pill-container" role="list">
      {validItems.length > 0
        ? validItems.map((it, i) => (
          <TimedPill
            key={i}
            id={`${idPrefix}-${i}`}
            label={it}
            tooltipText={getTooltip(it)}
            alignmentStatus={getItemAlignment(it)}
          />
        ))
        : <span className="pill empty">—</span>
      }
    </div>
  );
};

export default PillList;

